import * as deviceDetect from "deviceDetect";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import { letter as L } from "shared/unicodePatterns";
import * as browserTTSVoices from "../browserTTSVoices";
import {
  boundaryOffsetToPosition,
  type BoundaryTextPart,
} from "./boundaryPositions";
import type { TextPart as SpeakTextPart } from "./index";
import type {
  PlaybackHandle,
  TTSVoice as ProviderVoice,
  SpeakOptions,
  TTSProvider,
} from "./internalTypes";

const PROVIDER_ID = "browser";

/**
 * This contains alternate language codes that are used by the web speech API voices instead
 * of the language code we normally use.
 */
const alternativeLanguageCodes = {
  no: "nb",
};

// Canonical browser-provider keys are per-feature. The unified and
// pre-unification keys are legacy fallbacks so existing local picks keep
// working, and the Read Aloud bookmarklet still sees a browser voice.
const featureVoiceKey = (feature: SpeakOptions["feature"], language: string) =>
  `tts.voice.${feature}.${language}`;
const voiceKey = (language: string) => `tts.voice.${language}`;
const featureRateKey = (feature: SpeakOptions["feature"], language: string) =>
  `tts.rate.${feature}.${language}`;
const preUnificationVoiceKey = (language: string) =>
  `reader.readAloudVoice.${language}`;
const preUnificationRateKey = (language: string) =>
  `reader.readAloudRate.${language}`;

interface WebSpeechVoice {
  voiceURI: string;
  lang: string;
  name: string;
  localService: boolean;
  default: boolean;
}

/**
 * Builds the friendly display name for a Web Speech voice, applying the
 * macOS-Chrome nested-parenthesis fixup and appending the lang code only when
 * the voice name doesn't already hint at the language.
 */
const formatVoiceDisplayName = (name: string, lang: string): string => {
  const languageCode = lang.substring(0, 2);
  const languageInfo = languageMap.map[languageCode];

  if (languageInfo) {
    const nameLower = name.toLowerCase();
    if (
      nameLower.includes(languageInfo.name.toLowerCase()) ||
      nameLower.includes(languageInfo.english.toLowerCase())
    ) {
      const chromeVoiceNameRegExp = new RegExp(
        `([${L}]+) \\(([${L}]+) \\(([${L}]+)\\)\\)`,
        "u"
      );
      const match = chromeVoiceNameRegExp.exec(name);
      if (match) {
        return `${match[1]} (${match[2]}, ${match[3]})`;
      }
      return name;
    }
  }

  return `${name} (${lang})`;
};

/**
 * Synchronous lookup of a voice's friendly display name by voiceURI. Returns
 * undefined if the Web Speech API hasn't loaded voices yet or the URI isn't
 * known on this device. Use this for UI labels that need a name without
 * awaiting initialize().
 */
const getDisplayNameForVoiceURI = (voiceURI: string): string | undefined => {
  if (typeof window === "undefined" || !window.speechSynthesis)
    return undefined;
  const voices = window.speechSynthesis.getVoices() ?? [];
  const voice = voices.find((v) => v.voiceURI === voiceURI);
  if (!voice) return undefined;
  return formatVoiceDisplayName(voice.name, voice.lang);
};

/**
 * You need to call this before using TTS for a given language.
 * Returns list of voices for the given language.
 */
const initialize = async (
  language: string
): Promise<{
  ttsVoices: { voiceURI: string; lang: string; displayName: string }[];
  voiceSelected: boolean;
}> => {
  if (!isTTSSupported()) {
    return {
      ttsVoices: [],
      voiceSelected: false,
    };
  }

  // Start listening for late-arriving device voices so availability can recover
  // (see onVoicesChanged). Safe to call repeatedly — it attaches at most once.
  ensureGlobalVoicesListener();

  // Never block initialization on browser voice enumeration. tts.initialize()
  // awaits every provider via Promise.all, so a blocking wait here would stall
  // Azure-backed Read Aloud / Auto-Pronounce for up to 30s on browsers that
  // expose speechSynthesis but enumerate device voices asynchronously — even for
  // users who never use browser voices. The global voiceschanged listener drives
  // recovery once the voices actually arrive.
  const voices = await getVoicesForLanguage(language, { wait: false });

  const ttsVoices = voices.map((voice) => ({
    voiceURI: voice.voiceURI,
    lang: voice.lang,
    displayName: formatVoiceDisplayName(voice.name, voice.lang),
  }));

  const voiceSelected = (await getVoice(language, false, undefined, {
    wait: false,
  }))
    ? true
    : false;

  return {
    ttsVoices,
    voiceSelected,
  };
};

/** This assumes that the given @param voices are all of the correct language */
const pickDefaultVoice = (
  language: string,
  voices: { voiceURI: string; lang: string }[]
) => {
  switch (language) {
    case "es": {
      // Prefer European Spanish voice based on user's timezone
      const preferEuropean = dateUtils.getTimezoneOffset() < 180;
      let preferredVoice;

      if (preferEuropean) {
        preferredVoice =
          voices.find(
            (voice) =>
              voice.voiceURI === "Monica" || voice.voiceURI.startsWith("Mónica")
          ) ?? voices.find((voice) => voice.lang === "es-ES");
      } else {
        preferredVoice =
          voices.find((voice) => voice.voiceURI.startsWith("Paulina")) ??
          voices.find((voice) => voice.lang === "es-MX");
      }

      return preferredVoice ?? voices[0];
    }
    default:
      return voices[0];
  }
};

/**
 * Every subscriber routed through the legacy `onvoiceschanged` slot below. That
 * property holds a single handler, whereas we can have several concurrent
 * `voiceschanged` subscribers (the global listener plus one per waiting
 * getVoices call), so one dispatcher fans out to all of them.
 */
const legacyVoicesChangedListeners = new Set<() => void>();

/**
 * Subscribes to the Web Speech `voiceschanged` event; returns an unsubscribe
 * function.
 *
 * Older WebKit (through iPadOS 15) doesn't implement EventTarget on
 * `speechSynthesis`, so calling addEventListener on it throws a TypeError.
 * Because this module's importers subscribe while they're still evaluating, that
 * throw poisoned the whole bundle and left login stuck on "Loading Readlang..."
 * (forum 5207). Fall back to the legacy `onvoiceschanged` property there.
 */
const subscribeToVoicesChanged = (listener: () => void): (() => void) => {
  const synthesis = window.speechSynthesis;

  if (typeof synthesis.addEventListener === "function") {
    synthesis.addEventListener("voiceschanged", listener);
    return () => synthesis.removeEventListener("voiceschanged", listener);
  }

  legacyVoicesChangedListeners.add(listener);
  synthesis.onvoiceschanged = () => {
    // Copy first: a listener may unsubscribe while being dispatched.
    for (const each of [...legacyVoicesChangedListeners]) each();
  };
  return () => legacyVoicesChangedListeners.delete(listener);
};

const getVoices = (
  { wait }: { wait: boolean } = { wait: true }
): Promise<WebSpeechVoice[]> => {
  return new Promise((resolve) => {
    if (window.speechSynthesis === undefined) {
      resolve([]);
      return;
    }

    const voices = window.speechSynthesis.getVoices() ?? [];

    if (voices.length > 0) {
      resolve(voices);
      return;
    }

    // Voices aren't loaded yet. Callers about to speak (wait: true) must block
    // until a non-empty enumeration arrives, because speaking without a voice
    // fails. Initialization (wait: false) must not block — see initialize().
    // The global voiceschanged listener handles late-arriving voices for it.
    if (!wait) {
      resolve([]);
      return;
    }

    const cleanup = () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };

    // Chrome/Edge (notably on Android, and especially in installed PWAs) load
    // voices asynchronously and are known to fire `voiceschanged` one or more
    // times with a still-empty list before the real voices arrive. Resolving on
    // that first empty event would hand back [] and poison the per-language
    // cache, hiding Read Aloud until a full reload. So keep waiting until
    // getVoices() is actually non-empty, only falling back to whatever exists
    // once the timeout fires.
    const onVoicesChanged = () => {
      const loaded = window.speechSynthesis.getVoices() ?? [];
      if (loaded.length === 0) return;
      cleanup();
      resolve(loaded);
    };

    const unsubscribe = subscribeToVoicesChanged(onVoicesChanged);

    // If voices haven't loaded within 30 seconds, resolve with whatever the API
    // reports now — by this point they're often populated even on browsers that
    // never fired a second `voiceschanged`.
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(window.speechSynthesis.getVoices() ?? []);
    }, 30 * 1000);
  });
};

// Doing this because remote voices are not generated the onboundary event
const useLocalVoicesOnly = false;

/** Filters out bad voices on iOS */
const filteredVoices = (voices: any[]) => {
  // Use map to avoid duplicates
  const uniqueVoices = new Map<string, any>();

  for (const voice of voices) {
    // com.apple.eloquence voices all sound bad
    if (voice.voiceURI.startsWith("com.apple.eloquence")) {
      continue;
    }

    // If voice URI has already been added, skip
    if (uniqueVoices.has(voice.voiceURI)) {
      continue;
    }

    // com.apple.voice.super-compact voices can be OK, but if an equivalent compact voice exists,
    // use that instead
    if (voice.voiceURI.startsWith("com.apple.voice.super-compact")) {
      // If the compact voice exists, don't add this super-compact voice
      const equivalentCompactURI = voice.voiceURI.replace(
        "super-compact",
        "compact"
      );
      if (
        voices.some(
          (otherVoice) => otherVoice.voiceURI === equivalentCompactURI
        )
      ) {
        continue;
      }
    }

    uniqueVoices.set(voice.voiceURI, voice);
  }

  return Array.from(uniqueVoices.values()).sort((a, b) =>
    a.voiceURI < b.voiceURI ? -1 : 1
  );
};

const cachedVoicesForLanguage = new Map<string, WebSpeechVoice[]>();

// Subscribers notified when the set of device voices changes after the initial
// load. The availability layer uses this to recover from an early empty
// enumeration on browsers (Chrome/Edge Android PWAs) that populate voices
// asynchronously — without it, the Read Aloud button / voice picker stay hidden
// until a full page reload even once voices have arrived.
const voicesChangedListeners = new Set<() => void>();
let globalVoicesListenerAttached = false;
let lastSeenVoiceCount = 0;

const handleGlobalVoicesChanged = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const count = (window.speechSynthesis.getVoices() ?? []).length;
  // Dedupe: Chrome fires `voiceschanged` repeatedly; only react when the voice
  // list actually changed size (e.g. 0 → N once they finally load).
  if (count === lastSeenVoiceCount) return;
  lastSeenVoiceCount = count;
  // Drop cached per-language results so the next lookup re-enumerates against
  // the newly available voices.
  cachedVoicesForLanguage.clear();
  for (const listener of voicesChangedListeners) {
    listener();
  }
};

const ensureGlobalVoicesListener = () => {
  if (globalVoicesListenerAttached) return;
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  globalVoicesListenerAttached = true;
  lastSeenVoiceCount = (window.speechSynthesis.getVoices() ?? []).length;
  subscribeToVoicesChanged(handleGlobalVoicesChanged);
};

const onVoicesChanged = (listener: () => void): void => {
  voicesChangedListeners.add(listener);
  ensureGlobalVoicesListener();
};

const getVoicesForLanguage = async (
  language: string,
  { wait }: { wait: boolean } = { wait: true }
): Promise<WebSpeechVoice[]> => {
  const cachedResult = cachedVoicesForLanguage.get(language);
  if (cachedResult && cachedResult.length > 0) {
    return cachedResult;
  }

  const voices = await getVoices({ wait });
  const result = getMatchingVoicesForLanguage(language, voices);

  // Never cache an empty result. On browsers that load voices asynchronously
  // (Chrome/Edge Android PWAs) an early empty enumeration would otherwise be
  // cached forever, permanently hiding Read Aloud for this language until a full
  // page reload. Leaving it uncached lets a later call — or the voiceschanged
  // recovery below — re-enumerate once the device voices have actually loaded.
  if (result.length > 0) {
    cachedVoicesForLanguage.set(language, result);
  }

  return result;
};

const getMatchingVoicesForLanguage = (
  language: string,
  voices: WebSpeechVoice[]
): WebSpeechVoice[] => {
  const alternativeLanguageCode = alternativeLanguageCodes[language];
  return filteredVoices(
    voices.filter(
      (voice) =>
        (!useLocalVoicesOnly || voice.localService) &&
        (voice.lang.startsWith(language) ||
          (alternativeLanguageCode &&
            voice.lang.startsWith(alternativeLanguageCode)))
    )
  );
};

const webSpeechVoiceToProviderVoice = (
  voice: WebSpeechVoice
): ProviderVoice => ({
  provider: PROVIDER_ID,
  id: voice.voiceURI,
  displayName: formatVoiceDisplayName(voice.name, voice.lang),
});

const getCachedVoicesForLanguage = (language: string): WebSpeechVoice[] =>
  cachedVoicesForLanguage.get(language) ?? [];

const getLoadedVoicesForLanguage = (language: string): WebSpeechVoice[] =>
  typeof window === "undefined" || !window.speechSynthesis
    ? []
    : getMatchingVoicesForLanguage(
        language,
        window.speechSynthesis.getVoices() ?? []
      );

const getStoredVoiceId = (
  feature: SpeakOptions["feature"],
  language: string
): string | undefined =>
  localStorage.getItem(featureVoiceKey(feature, language)) ||
  localStorage.getItem(voiceKey(language)) ||
  localStorage.getItem(preUnificationVoiceKey(language)) ||
  undefined;

const setStoredVoiceId = (
  feature: SpeakOptions["feature"],
  language: string,
  voiceId: string
): void => {
  localStorage.setItem(featureVoiceKey(feature, language), voiceId);
  if (
    feature === "readAloud" ||
    !localStorage.getItem(preUnificationVoiceKey(language))
  ) {
    localStorage.setItem(preUnificationVoiceKey(language), voiceId);
  }
};

const parseStoredRate = (key: string): number | undefined => {
  const value = localStorage.getItem(key);
  if (!value) return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};

const normalize = (text: string) => {
  return text.replace(/’/g, "'").replace(/\n+/g, "\n").replace(/["""]/g, "");
};

const prepareTextParts = (
  textParts: SpeakTextPart[]
): { text: string; parts: BoundaryTextPart[] } => {
  const parts: BoundaryTextPart[] = [];
  let text = "";
  let currentCharIndex = 0;

  for (let index = 0; index < textParts.length; index++) {
    const part = textParts[index];

    let normalizedText = part.text ? normalize(part.text) : "";

    if (!part.text) {
      console.log("empty text part: ", part);
    }

    if (index === textParts.length - 1) {
      normalizedText = cleanTextForTTS(normalizedText);
    }

    text += normalizedText;

    parts.push({
      text: normalizedText,
      start: currentCharIndex,
      end: currentCharIndex + normalizedText.length,
      position: part.position,
    });
    currentCharIndex += normalizedText.length;
  }

  return { text, parts };
};

const cleanTextForTTS = (text: string) => {
  // Replace additional punctuation at the end of the text:
  return text.replace(/[.!?][ .!?].$/, "");
};

/**
 * Some Web Speech voices (e.g. Google Español on Chrome/Android) don't fire
 * `boundary` events for each word. Read Aloud needs to know this so it can
 * split page-crossing sentences at page boundaries instead of waiting for
 * boundary callbacks that will never arrive.
 */
const VOICE_BOUNDARY_SUPPORT_KEY_PREFIX = "browserTTS.voiceBoundarySupport.";

const setBrowserVoiceBoundarySupport = (
  voiceId: string,
  supported: boolean
): void => {
  localStorage.setItem(
    VOICE_BOUNDARY_SUPPORT_KEY_PREFIX + voiceId,
    supported ? "true" : "false"
  );
};

/**
 * If we don't get more than one boundary event after this long, assume the
 * voice doesn't fire word-by-word boundary events.
 */
const BOUNDARY_DETECTION_TIMEOUT_MS = 2500;

/** Minimum word count to be confident that 0-1 boundary events means no support. */
const BOUNDARY_DETECTION_MIN_WORDS = 4;

let onEndHandler: () => void;

const playTTSSimple = async (
  languageCode: string,
  text: string,
  /**
   * User-facing rate (0.6..1.5). Passed through from the unified play()
   * entry point. Optional for legacy callers that still rely on the
   * localStorage rate; new callers should provide it directly so we don't
   * round-trip the rate through localStorage (which previously caused
   * cross-feature bleed via the pre-unification rate key).
   */
  rate?: number,
  /** Voice URI to use for playback without mutating stored preferences. */
  voiceURI?: string,
  /**
   * Polled after voice resolution (which can await getVoices()/voiceschanged
   * for up to 30s on iOS Safari) to bail before speaking if playback was
   * cancelled during that wait — speechSynthesis.cancel() can't stop an
   * utterance we haven't queued yet, so without this we'd talk over the card
   * the user has since moved on to.
   */
  isCancelled?: () => boolean
): Promise<boolean> => {
  cancelTTS();
  const voice = await getVoice(languageCode, true, voiceURI);

  // Bail out if no voice can be resolved for this language. Calling
  // speechSynthesis.speak() with utterance.voice=null causes Chrome Android
  // to reuse the previously-used voice (bleed-through across languages —
  // e.g. Māori spoken with a Russian accent).
  if (!voice) {
    return false;
  }

  if (isCancelled?.()) {
    return false;
  }

  const utterance = new SpeechSynthesisUtterance(text);
  // Set utterance.lang to the chosen voice's full lang (e.g. "es-MX") rather
  // than the two-letter code. On macOS some voices (Mónica, Paulina) otherwise
  // fall back to English prosody when given an ambiguous two-letter code.
  utterance.lang = voice.lang;
  utterance.rate = applyExtraRateFactor(rate ?? readLegacyRate(languageCode));
  utterance.voice = voice;
  window.speechSynthesis.speak(utterance);
  return true;
};

const getVoice = async (
  languageCode: string,
  fallback: boolean = true,
  /**
   * Explicit voice URI to look up, bypassing the localStorage-stored
   * selection. Used by callers that want to play a specific voice (e.g.
   * picker previews) without persisting the choice via browserTTSVoices.
   */
  voiceURIOverride?: string,
  /**
   * Whether to block on browser voice enumeration when voices haven't loaded
   * yet. Speak callers wait (the default); initialization passes false so it
   * never stalls tts.initialize(). See getVoices().
   */
  { wait }: { wait: boolean } = { wait: true }
): Promise<SpeechSynthesisVoice> => {
  const voiceURI = voiceURIOverride ?? browserTTSVoices.getVoice(languageCode);
  const alternativeLanguageCode = alternativeLanguageCodes[languageCode];

  if (voiceURI) {
    const voice = (await getVoices({ wait })).filter(
      (candidateVoice) =>
        (candidateVoice.lang.startsWith(languageCode) ||
          (alternativeLanguageCode &&
            candidateVoice.lang.startsWith(alternativeLanguageCode))) &&
        candidateVoice.voiceURI === voiceURI
    )[0];

    if (voice) {
      return voice;
    }
  }

  if (!fallback) {
    return undefined;
  }

  // No voice was found, just pick the first one that matches the language
  return pickDefaultVoice(
    languageCode,
    await getVoicesForLanguage(languageCode, { wait })
  );
};

/** Multiplied into the requested rate since browser voices tend to be too fast. */
const EXTRA_RATE_FACTOR = 0.85;

const applyExtraRateFactor = (userFacingRate: number): number =>
  userFacingRate * EXTRA_RATE_FACTOR;

/** Legacy localStorage rate read — only used when no explicit rate is passed. */
const readLegacyRate = (languageCode: string): number => {
  const value = parseFloat(browserTTSVoices.getRate(languageCode));
  return isNaN(value) ? 0.8 : value;
};

const playPositionedText = async (
  languageCode: string,
  textParts: SpeakTextPart[],
  onBoundary?: (position: number) => void,
  onEnd?: (nextPosition: number) => void,
  onError?: () => void,
  /** See playTTSSimple's `rate` param — passed directly from the unified play(). */
  rate?: number,
  /** See playTTSSimple's `voiceURI` param — preview-friendly voice override. */
  voiceURI?: string,
  /** See playTTSSimple's `isCancelled` param — bail before speaking stale audio. */
  isCancelled?: () => boolean
) => {
  const { text, parts } = prepareTextParts(textParts);

  // Check if speech synthesis is supported
  if (!("speechSynthesis" in window)) {
    console.error("Your browser does not support speech synthesis.");
    return;
  }

  const resolvedVoice = await getVoice(languageCode, true, voiceURI);

  // Bail out if no voice can be resolved for this language. Calling
  // speechSynthesis.speak() with utterance.voice=null causes Chrome Android
  // to reuse the previously-used voice (bleed-through across languages —
  // e.g. Māori spoken with a Russian accent).
  if (!resolvedVoice) {
    onError?.();
    return;
  }

  if (isCancelled?.()) {
    return;
  }

  const utterance = new SpeechSynthesisUtterance(text);

  utterance.voice = resolvedVoice;
  // Use the voice's full lang (e.g. "es-MX") to avoid English prosody on
  // macOS voices like Mónica/Paulina under an ambiguous two-letter language
  // code.
  utterance.lang = resolvedVoice.lang;
  utterance.rate = applyExtraRateFactor(rate ?? readLegacyRate(languageCode));

  const reversedParts = parts.slice().reverse();
  let boundaryEventCount = 0;
  let detectionTimeout: ReturnType<typeof setTimeout> | undefined;
  const wordCount = (text.match(/\S+/g) ?? []).length;
  const detectedVoiceId = utterance.voice?.voiceURI;

  const finalizeBoundarySupportDetection = () => {
    if (!detectedVoiceId) return;
    if (boundaryEventCount > 1) {
      setBrowserVoiceBoundarySupport(detectedVoiceId, true);
    } else if (wordCount >= BOUNDARY_DETECTION_MIN_WORDS) {
      setBrowserVoiceBoundarySupport(detectedVoiceId, false);
    }
  };

  utterance.onstart = () => {
    detectionTimeout = setTimeout(
      finalizeBoundarySupportDetection,
      BOUNDARY_DETECTION_TIMEOUT_MS
    );
  };

  utterance.onboundary = (event) => {
    boundaryEventCount++;
    const position = boundaryOffsetToPosition(parts, event.charIndex);
    if (position !== undefined) {
      onBoundary?.(position);
    }
  };

  onEndHandler = () => {
    clearTimeout(detectionTimeout);
    finalizeBoundarySupportDetection();
    const lastPart = reversedParts.find((part) => part.position !== undefined);
    onEnd?.(lastPart ? lastPart.position + 1 : undefined);
  };
  utterance.addEventListener("end", onEndHandler);
  utterance.addEventListener("error", () => {
    clearTimeout(detectionTimeout);
    onError?.();
  });

  // Trying this in case it fixes issue on iOS Safari: https://stackoverflow.com/a/35935851
  window.READLANG = window.READLANG ?? {};
  window.READLANG.utterance = utterance;

  // Use the Web Speech API to play the utterance
  window.speechSynthesis.speak(utterance);

  // Analytics is now fired by the unified tts.play() entry point so the
  // event lives in one place across browser + Azure providers. See
  // tts/play.ts for the `readAloud` trackEventWithProperties call.
};

const cancelTTS = () => {
  if ("speechSynthesis" in window) {
    // Remove onend handler to prevent next sentence from being read upon cancel
    window.READLANG?.utterance?.removeEventListener("end", onEndHandler);
    window.speechSynthesis.cancel();
  } else {
    console.error("Your browser does not support speech synthesis.");
  }
};

/**
 * Checks whether the Web Speech API is usable on this browser at all. Doesn't
 * check that a suitable voice is actually available, nor any per-language
 * policy (see supportsLanguage for the noWordBoundaries gate). Synchronous so
 * it can gate the synchronous supportsLanguage() availability check as well as
 * initialize().
 */
const isTTSSupported = (): boolean => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    return false;
  }

  // Regex to match the Firefox browser version number in the useragent string (if it exists)
  const firefoxVersionMatch = navigator.userAgent.match(/Firefox\/(\d+)/);

  // Check if Firefox version is at least 117 (when I can verify that speech synthesis works, in an
  // earlier version setting specific voices didn't work)
  if (firefoxVersionMatch && parseInt(firefoxVersionMatch[1], 10) < 117) {
    return false;
  }

  return true;
};

export const browserProvider: TTSProvider = {
  id: PROVIDER_ID,
  storage: "local",
  group: {
    id: "device",
    // The iOS app's web view speaks with the phone's own voices
    label: deviceDetect.isIOSApp() ? "Device voices" : "Browser voices",
    rank: 1,
  },
  minTierByFeature: { readAloud: "free", speakingMode: "free" },

  async initialize(language: string): Promise<void> {
    await initialize(language);
  },

  supportsLanguage(
    language: string,
    feature: SpeakOptions["feature"]
  ): boolean {
    // Honour the same browser/Firefox capability gate as initialize() before
    // trusting loaded voices.
    if (!isTTSSupported()) {
      return false;
    }
    // Browser voices for noWordBoundaries languages (zh, zh-TW, ja, th, yue)
    // don't fire word-boundary events. Auto-Pronounce (speakingMode) relies on
    // per-word highlighting, so it stays disabled for these languages. Read
    // Aloud only needs plain playback, so it remains available with whatever
    // device voice exists — otherwise non-Plus users lose the Read Aloud button
    // and voice picker entirely on Chinese/Japanese/etc. (task 00361).
    if (
      feature === "speakingMode" &&
      languageMap.map[language]?.noWordBoundaries
    ) {
      return false;
    }
    return (
      getCachedVoicesForLanguage(language).length > 0 ||
      getLoadedVoicesForLanguage(language).length > 0
    );
  },

  listVoices(language: string): ProviderVoice[] {
    return getCachedVoicesForLanguage(language).map(
      webSpeechVoiceToProviderVoice
    );
  },

  getDefaultVoice(language: string): ProviderVoice | undefined {
    const voice = pickDefaultVoice(
      language,
      getCachedVoicesForLanguage(language)
    );
    return voice ? webSpeechVoiceToProviderVoice(voice) : undefined;
  },

  getVoice(language: string, voiceId: string): ProviderVoice | undefined {
    const voice = getCachedVoicesForLanguage(language).find(
      (candidate) => candidate.voiceURI === voiceId
    );
    return voice
      ? webSpeechVoiceToProviderVoice(voice)
      : {
          provider: PROVIDER_ID,
          id: voiceId,
          displayName: getDisplayNameForVoiceURI(voiceId) ?? voiceId,
        };
  },

  getVoiceBoundarySupport(
    _language: string,
    voiceId: string
  ): boolean | undefined {
    const value = localStorage.getItem(
      VOICE_BOUNDARY_SUPPORT_KEY_PREFIX + voiceId
    );
    if (value === "true") return true;
    if (value === "false") return false;
    return undefined;
  },

  onVoicesChanged,

  getLocalVoiceId: getStoredVoiceId,
  setLocalVoiceId: setStoredVoiceId,

  getLocalRate(feature: SpeakOptions["feature"], language: string) {
    return (
      parseStoredRate(featureRateKey(feature, language)) ??
      (feature === "readAloud"
        ? parseStoredRate(preUnificationRateKey(language))
        : undefined)
    );
  },

  setLocalRate(
    feature: SpeakOptions["feature"],
    language: string,
    rate: number
  ) {
    localStorage.setItem(featureRateKey(feature, language), String(rate));
  },

  async speak(opts: SpeakOptions): Promise<PlaybackHandle> {
    const {
      language,
      text,
      voice,
      rate,
      minStartDelayMs,
      onStart,
      onBoundary,
      onEnd,
      onError,
      abort,
    } = opts;

    // Voice and rate are passed directly so preview playback never mutates
    // legacy localStorage keys. Persisting real user picks lives in
    // voiceStore.setVoice.
    const voiceURI =
      voice && voice.provider === PROVIDER_ID ? voice.id : undefined;

    // Web Speech API has no fetch to overlap, so the delay is just a plain
    // wait — keeps behaviour consistent with Azure when Read Aloud asks for
    // an inter-sentence pause.
    let cancelled = abort?.aborted ?? false;
    const handle: PlaybackHandle = {
      stop: () => {
        cancelled = true;
        cancelTTS();
      },
    };
    // Register synchronously so play.cancel() can abort us during the
    // pre-playback delay — see azureProvider for the same pattern.
    if (abort) abort.stop = handle.stop;
    if (typeof minStartDelayMs === "number" && minStartDelayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, minStartDelayMs));
      if (cancelled) return handle;
    }

    onStart?.();

    if (typeof text === "string") {
      // Simple path — no per-word boundaries available or needed.
      const started = await playTTSSimple(
        language,
        text,
        rate,
        voiceURI,
        () => cancelled
      );
      // Cancelled while voices were loading — playTTSSimple bailed before
      // speaking, so stay silent (no onError/onEnd for a deliberate cancel).
      if (cancelled) {
        return handle;
      }
      if (!started) {
        onError?.(new Error(`No browser TTS voice available for ${language}`));
        return handle;
      }
      onEnd?.();
      return handle;
    }

    await playPositionedText(
      language,
      text,
      (position) => onBoundary?.(position),
      (nextPosition) => onEnd?.(nextPosition),
      () => onError?.(new Error("browser TTS error")),
      rate,
      voiceURI,
      () => cancelled
    );

    return handle;
  },
};

export const _functionsForUnitTests = {
  /**
   * Resets the device-voice cache and the global voiceschanged listener state so
   * each test starts fresh. Deliberately keeps the registered listeners (the
   * availability layer subscribes once at module load) — only the DOM
   * attachment flag and cached snapshots are cleared.
   */
  resetVoicesState() {
    cachedVoicesForLanguage.clear();
    globalVoicesListenerAttached = false;
    lastSeenVoiceCount = 0;
    legacyVoicesChangedListeners.clear();
  },
  subscribeToVoicesChanged,
};
